// ABOUTME: Checkout settings view for shipper sidepanel (warehouse side).
// ABOUTME: Allows enabling webshop checkout for a shipper, selecting supported countries and, for broker shippers (Innosend V2, Sendcloud), the brands offered.

import { Box, ButtonBase, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import cx from "classnames";
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import { tokens } from "../../../../../theme";
import { useEditShipperMutation, useGetConfigInfoQuery } from "../../../../../newapi/shipper/shipperSlice";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../../utils/permissions';
import { countriesFromLocale, regions } from "../../../../../utils/countries";
import { isCheckoutBroker, checkoutBrandsFromConfigInfo } from "../../../../../utils/shipperValidation";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import Shbutton from "../../../../theme/buttons/Shbutton";
import Dropdown from "../../../../theme/dropout/DropOut";
import { InputDropdown } from "../../../../theme/dropdowns/InputDropdown";
import Text from "../../../../theme/text/Text";
import Subheading from "../../../../theme/text/Subheading";
import SpDesktopFooter from "../../../../theme/sidepanel2/SidepanelFooter";
import SHQuickAction from "../../../../theme/Modal/SHQuickAction";

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <ButtonGroup version={2}>
            {props.hasModifyPermission && (
                <Shbutton
                    isLoading={props.isLoading}
                    className={"group-btn"}
                    onClick={props.action}
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    variant="contained"
                    endIcon={<SaveIcon />}
                >
                    {t('save')}
                </Shbutton>
            )}
        </ButtonGroup>
    );
};

const CheckoutSettings = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const hasModifyPermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.SHIPPER_MANAGE, UserPrivileges.SETTINGS_ALL])
    );

    const language = useSelector((state) => state.settings.language);
    const countries = countriesFromLocale(language);

    const [webshopCheckoutEnabled, setWebshopCheckoutEnabled] = useState(false);
    const [selectedCountries, setSelectedCountries] = useState([]);

    // Brokers (Innosend, Sendcloud) carry several brands; the warehouse picks which of them relations may offer.
    const isBroker = isCheckoutBroker(props.data?.type);
    const [selectedCouriers, setSelectedCouriers] = useState([]);
    const { data: configInfo } = useGetConfigInfoQuery(props.data?.id, { skip: !isBroker || !props.data?.id });

    // Whether the broker can deliver to service points at all, from the freshest config info.
    const servicePointDelivery = configInfo?.configInfo?.servicePointDelivery;

    // One entry per brand the broker's config info offers, in the shape the shipper stores
    const couriers = useMemo(() => checkoutBrandsFromConfigInfo(props.data?.type, configInfo), [props.data?.type, configInfo]);

    // A brand without service points is flagged in its label; the saved entry itself carries no label.
    const courierOptions = useMemo(() => couriers.map(courier => ({
        ...courier,
        label: courier.servicePoints === false ? `${courier.name} (${t('checkout_no_pickup_points')})` : courier.name
    })), [couriers, t]);

    const selectedCourierIndices = useMemo(
        () => selectedCouriers.map(courier => couriers.findIndex(option => option.key === courier.key)).filter(index => index !== -1),
        [selectedCouriers, couriers]
    );

    const handleCourierChange = (selectedIndices) => {
        setSelectedCouriers(selectedIndices.map(index => couriers[index]).filter(Boolean));
    };

    const [EditShipper, { isLoading }] = useEditShipperMutation();

    useEffect(() => {
        if (props.data) {
            setWebshopCheckoutEnabled(props.data.webshopCheckoutEnabled || false);
            setSelectedCountries(props.data.webshopCheckoutCountries || []);
            setSelectedCouriers(props.data.webshopCheckoutCouriers || []);
        }
    }, [props.data]);

    const handleCountryChange = (selectedIndices) => {
        if (!countries) return;
        const selectedCodes = selectedIndices.map(index => countries[index].alpha2);
        setSelectedCountries(selectedCodes);
    };

    const getSelectedCountryIndices = () => {
        if (!countries || !selectedCountries.length) return [];
        return selectedCountries
            .map(code => countries.findIndex(c => c.alpha2 === code))
            .filter(index => index !== -1);
    };

    const getSelectedCountryNames = () => {
        if (!countries || !selectedCountries.length) return [];
        return selectedCountries
            .map(code => {
                const country = countries.find(c => c.alpha2 === code);
                return country ? country.name : null;
            })
            .filter(name => name !== null);
    };

    const selectRegion = (regionIndex) => {
        const countryCodes = regions[regionIndex]?.countries || [];
        const regionIndices = countryCodes
            .map(code => countries.findIndex(c => c.alpha2 === code))
            .filter(index => index !== -1);

        const currentIndices = getSelectedCountryIndices();
        const isSameSelection =
            regionIndices.length === currentIndices.length &&
            regionIndices.every(index => currentIndices.includes(index));

        if (isSameSelection) {
            handleCountryChange([]);
        } else {
            handleCountryChange(regionIndices);
        }
    };

    const isRegionActive = (regionIndex) => {
        const countryCodes = regions[regionIndex]?.countries || [];
        const regionIndices = countryCodes
            .map(code => countries.findIndex(c => c.alpha2 === code))
            .filter(index => index !== -1);

        if (regionIndices.length === 0) return false;
        const currentIndices = getSelectedCountryIndices();
        return regionIndices.every(index => currentIndices.includes(index));
    };

    const handleSaveClick = useCallback(async () => {
        try {
            if (isLoading) return;
            const response = await EditShipper({
                id: props.data?.id,
                webshopCheckoutEnabled,
                webshopCheckoutCountries: selectedCountries,
                ...(isBroker ? { webshopCheckoutCouriers: selectedCouriers } : {}),
            }).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    }, [isLoading, props.data?.id, webshopCheckoutEnabled, selectedCountries, isBroker, selectedCouriers, EditShipper, t]);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions: <GridActions
                isLoading={isLoading}
                hasModifyPermission={hasModifyPermission}
                action={handleSaveClick}
            />,
        });

        return () => {};
    }, [props.data?.id, handleSaveClick, hasModifyPermission]);

    return (
        <Box height={1} display={"flex"} flexDirection={"column"} paddingX={3}
            sx={{
                "@media screen and (min-width: 48.1em)": {
                    marginBottom: 11
                }
            }}
        >
            <Dropdown title={t("checkout_settings")} open>
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    paddingTop: 3,
                }}>
                    <Box
                        display={"flex"}
                        flexDirection={"column"}
                        sx={{
                            gap: 1,
                            maxWidth: 160,
                            "@media screen and (max-width:47.9375em)": {
                                width: 1,
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                                maxWidth: "unset",
                            }
                        }}
                    >
                        <Text>{t("webshop_checkout_enabled")}</Text>
                        <ToggleButtonGroup
                            color={webshopCheckoutEnabled ? "green" : "red"}
                            value={webshopCheckoutEnabled ? "true" : "false"}
                            exclusive
                            onChange={(e) => setWebshopCheckoutEnabled(e.target.value === "true")}
                            aria-label="Checkout enabled"
                            className={cx(`checkout-enabled`, {
                                'active': webshopCheckoutEnabled
                            })}
                            sx={{
                                background: colors.red[100],
                                padding: 0.5,
                                borderRadius: 5,
                                transition: "250ms",
                                "&.active": {
                                    background: colors.green[100],
                                    transition: "250ms",
                                },
                            }}
                        >
                            <ToggleButton
                                className={cx(``, {
                                    'active': !webshopCheckoutEnabled
                                })}
                                sx={{
                                    padding: 0,
                                    paddingX: 2,
                                    border: 0,
                                    lineHeight: "32px",
                                    textTransform: "unset",
                                    borderRadius: "32px !important",
                                    color: colors.green[400],
                                    transition: "250ms",
                                    "&.active, &.Mui-selected, &.Mui-selected:hover": {
                                        background: colors.red[400],
                                        color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                    }
                                }}
                                value={"false"}>
                                {t('inactive')}
                            </ToggleButton>
                            <ToggleButton
                                className={cx(``, {
                                    'active': webshopCheckoutEnabled
                                })}
                                sx={{
                                    padding: 0,
                                    paddingX: 2,
                                    border: 0,
                                    lineHeight: "32px",
                                    textTransform: "unset",
                                    borderRadius: "32px !important",
                                    color: colors.red[400],
                                    transition: "250ms",
                                    "&.active,&.Mui-selected,&.Mui-selected:hover": {
                                        background: colors.green[400],
                                        color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                    }
                                }}
                                value={"true"}
                            >
                                {t('active')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {webshopCheckoutEnabled && (
                        <SHQuickAction
                            component={
                                <Box sx={{ ml: 0.5 }}>
                                    <Subheading sx={{ mt: 0.5 }}>
                                        {t("selected_country")}
                                    </Subheading>
                                    <Box
                                        sx={{
                                            mt: 1,
                                            maxHeight: 500,
                                            width: 300,
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: "12px 16px",
                                            mb: 2,
                                        }}
                                    >
                                        {getSelectedCountryNames()?.map((name, idx) => (
                                            <Box key={idx}>
                                                <Text>{name}</Text>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            }
                        >
                            {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                                <Box
                                    aria-owns={anchorEl ? "simple-popover" : undefined}
                                    aria-haspopup="true"
                                    onMouseEnter={selectedCountries.length > 0 ? onMouseEnter : null}
                                    onMouseLeave={selectedCountries.length > 0 ? onMouseLeave : null}
                                    onTouchStart={selectedCountries.length > 0 ? onTouchStart : null}
                                    sx={{
                                        width: 1,
                                        paddingTop: 2,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Text>{t("checkout_countries")}</Text>
                                    <Box sx={{ width: 1, mt: 1.5 }}>
                                        <Box sx={{ mb: 1.5, display: "flex", gap: 1, flexDirection: 'column' }}>
                                            {regions.map((region, idx) => (
                                                <ButtonBase
                                                    key={idx}
                                                    className={cx(undefined, {
                                                        'is-active': isRegionActive(idx),
                                                    })}
                                                    onClick={() => selectRegion(idx)}
                                                    sx={{
                                                        px: 1.5,
                                                        height: 24,
                                                        borderRadius: 3,
                                                        width: 1,
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'center',
                                                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                                        boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                                                        color: colors.txt['primary'],
                                                        fontWeight: 500,
                                                        fontFamily: "Roboto,sans-serif",
                                                        "& .region-icon": { display: "none" },
                                                        "&:hover": {
                                                            background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[50],
                                                        },
                                                        "&.is-active": {
                                                            background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                                            borderColor: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                                            color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                                            justifyContent: 'space-between',
                                                            "& .region-icon": { display: "block" },
                                                        },
                                                    }}
                                                >
                                                    {region.name}
                                                    <CheckIcon fontSize='small' className="region-icon" />
                                                </ButtonBase>
                                            ))}
                                        </Box>
                                        <InputDropdown
                                            name="checkoutCountries"
                                            multiple={true}
                                            noSelection={t('choose_countries')}
                                            displayName="name"
                                            searchOptions={{
                                                enabled: true,
                                                placeholder: t("search")
                                            }}
                                            options={countries ?? []}
                                            selected={getSelectedCountryIndices()}
                                            onChange={handleCountryChange}
                                            closeOnSelection={false}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </SHQuickAction>
                    )}

                    {webshopCheckoutEnabled && isBroker && (
                        <Box sx={{ width: 1, paddingTop: 2, display: "flex", flexDirection: "column" }}>
                            {servicePointDelivery?.known === true && !servicePointDelivery.enabled && (
                                <Text>{t("checkout_servicepoints_disabled")}</Text>
                            )}
                            <Text>{t("checkout_couriers")}</Text>
                            <Box sx={{ width: 1, mt: 1.5 }}>
                                <InputDropdown
                                    name="checkoutCouriers"
                                    multiple={true}
                                    noSelection={t('choose_couriers')}
                                    displayName="label"
                                    options={courierOptions}
                                    selected={selectedCourierIndices}
                                    onChange={handleCourierChange}
                                    closeOnSelection={false}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            </Dropdown>

            <SpDesktopFooter>
                <GridActions
                    isLoading={isLoading}
                    hasModifyPermission={hasModifyPermission}
                    action={handleSaveClick}
                />
            </SpDesktopFooter>
        </Box>
    );
};

export default CheckoutSettings;
